import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Divider,
  TextField,
  FormHelperText,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "../../components/sidebar/Sidebar";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import UserCampaignsTable from "../../components/campaign/UserCampaignsTable";
import AssignedCampaignsTable from "../../components/campaign/AssignedCampaignsTable";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { useGetUserCompaniesQuery } from "../../store/apis/companyApi";
import { components } from "../../types/api";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useGetCurrenciesQuery } from "../../store/apis/currencyApi";
import {
  useCreateCampaignMutation,
  useGetCampaignFormatsQuery,
  useGetUserCampaignsQuery,
} from "../../store/apis/campaignApi";
import { AssignedCampaign } from "../../types/campaign";
import { useUserRole } from "../../hooks/useUserRole";
import dayjs from "dayjs";
import { CampaignGoals } from "@store/slices/campaign/campaignTypes";
import CampaignMergeEditor from "../../components/campaign/CampaignMergeEditor";
import * as Sentry from "@sentry/react";
import {
  CTV_SOURCE_PLATFORMS,
  isDv360InternalPlatform,
  mapPlatformToInternalPlatform,
  resolveAssignedInternalPlatform,
} from "../../utils/internalPlatformUtils";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type Platform = components["schemas"]["_36_Enums.Platform"];

// Define a type for the company with proper fields
type Company = components["schemas"]["Company"];

// Add name property for display purposes
interface CompanyWithDisplay extends Company {
  nameCommercial: string;
  nameFiscal: string;
}

// Using Platform enum from API types

// Define the type for a campaign
type Campaign = components["schemas"]["StandardizedCampaign"];

type ParsedOriginalData = Record<string, unknown>;

const findFirstNonEmptyString = (values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

const parseCampaignOriginalData = (
  originalData: unknown
): ParsedOriginalData | undefined => {
  if (!originalData) {
    return undefined;
  }

  if (typeof originalData === "string") {
    try {
      return JSON.parse(originalData) as ParsedOriginalData;
    } catch (error) {
      console.error("Failed to parse campaign originalData", error);
      return undefined;
    }
  }

  if (typeof originalData === "object") {
    return originalData as ParsedOriginalData;
  }

  return undefined;
};

const getDv360Identifiers = (
  campaign: Campaign
): { advertiserId?: string; campaignId?: string } => {
  const parsedOriginal = parseCampaignOriginalData(campaign.originalData);

  if (!parsedOriginal) {
    return { campaignId: campaign.id };
  }

  const advertiserObject = parsedOriginal.advertiser as
    | ParsedOriginalData
    | undefined;
  const advertiserId = findFirstNonEmptyString([
    parsedOriginal.advertiserId,
    parsedOriginal.advertiser_id,
    advertiserObject?.advertiserId,
    advertiserObject?.id,
  ]);

  const campaignObject = parsedOriginal.campaign as
    | ParsedOriginalData
    | undefined;
  const campaignId =
    findFirstNonEmptyString([
      parsedOriginal.campaignId,
      parsedOriginal.campaign_id,
      campaignObject?.campaignId,
      campaignObject?.id,
    ]) ?? campaign.id;

  return { advertiserId, campaignId };
};

const NewCampaign: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [internalCampaigns, setInternalCampaigns] = useState<
    AssignedCampaign[]
  >([]);
  const [internalCampaignMerges, setInternalCampaignMerges] = useState<
    { name: string; internalCampaignIds: string[] }[]
  >([]);
  const [dv360InsertionOrderSelections, setDv360InsertionOrderSelections] =
    useState<Record<string, string[]>>({});
  const [dv360SelectionErrors, setDv360SelectionErrors] = useState<
    Record<string, string>
  >({});
  const [internalSourcePlatform, setInternalSourcePlatform] =
    useState<Platform>("DV360");
  const [campaignData, setCampaignData] = useState({
    displayName: "",
    platform: "DV360" as Platform,
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().add(7, "day").format("YYYY-MM-DD"),
    goal: "CPC",
    currency_enum: "EUR" as const,
    format: "ALL" as components["schemas"]["CampaignFormats"],
    visibleToClients: true,
  });
  const isCtvParent = campaignData.platform === "CTV";
  const theme = useTheme();
  const { t } = useTranslation();
  const { isClient } = useUserRole();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getPlatformLabel = (platform: Platform): string => {
    switch (platform) {
      case "DV360":
        return t("platforms.dv360");
      case "NATADS":
        return t("platforms.natads");
      case "SPOTIFY_ADS":
        return t("platforms.spotify");
      case "HIVESTACK":
        return t("platforms.hivestack");
      case "AMAZON_DSP":
        return t("platforms.amazonDsp", { defaultValue: "Amazon DSP" });
      case "CTV":
        return t("platforms.ctv", { defaultValue: "CTV" });
      case "META":
        return t("platforms.meta", { defaultValue: "Meta" });
      case "TIKTOK":
        return t("platforms.tiktok", { defaultValue: "TikTok" });
      case "GOOGLE_ADS":
        return t("platforms.googleAds", { defaultValue: "Google Ads" });
      case "REDDIT":
        return t("platforms.reddit", { defaultValue: "Reddit" });
      default:
        return platform;
    }
  };

  // Get the user from the session slice
  const userDb = useSelector(selectUserDb);

  // Fetch companies assigned to the user
  const {
    data: companiesData,
    isLoading: isLoadingCompanies,
    error: companiesError,
    refetch: refetchCompanies,
  } = useGetUserCompaniesQuery();

  // Effect to refetch companies when user changes
  useEffect(() => {
    if (userDb?.id) {
      refetchCompanies();
    }
  }, [userDb?.id, refetchCompanies]);

  // Campaign API mutation
  const [
    createCampaign,
    { isLoading: isCreating, error: createError, isSuccess: createSuccess },
  ] = useCreateCampaignMutation();

  useEffect(() => {
    if (createSuccess) {
      dispatch(
        setToastMessage({
          message: t("campaigns.createSuccess"),
          severity: "success",
        })
      );
      navigate("/campaigns");
    }
  }, [createSuccess, dispatch, navigate, t]);

  // Show error toast when createError occurs
  useEffect(() => {
    if (createError) {
      // Log the detailed error for debugging purposes only
      console.error("Campaign creation error:", createError);

      // Show a generic error message to the user
      dispatch(
        setToastMessage({
          message: t("campaigns.createError"),
          severity: "error",
        })
      );
    }
  }, [createError, dispatch, t]);

  // State for campaign filter platform
  const [filterPlatform, setFilterPlatform] = useState<Platform>("DV360");

  const { data: campaignFormatsData, isLoading: isLoadingCampaignFormats } =
    useGetCampaignFormatsQuery(undefined, {
      skip: !userDb?.id || filterPlatform !== "DV360",
    });

  // Fetch user campaigns with the selected platform and company filter
  const {
    data: userCampaignsData,
    isLoading: isLoadingUserCampaigns,
    error: userCampaignsError,
  } = useGetUserCampaignsQuery(
    {
      platform: filterPlatform,
      companyId: selectedCompany,
    },
    {
      // Skip the query if either platform or companyId is not selected
      skip: !selectedCompany,
      // Refetch when component mounts or when dependencies change
      refetchOnMountOrArgChange: true,
    }
  );

  // Log API response data for debugging and show error toasts
  useEffect(() => {
    if (userCampaignsError) {
      console.error("User campaigns error:", userCampaignsError);
    }

    const responseErrors = userCampaignsData?.data?.errors || [];
    const rootMessage = userCampaignsData?.message;
    // Some endpoints may return an 'error' field in the root payload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rootError = (userCampaignsData as any)?.error;

    if (
      responseErrors.length > 0 ||
      rootError ||
      (rootMessage && userCampaignsData?.data?.campaigns?.length === 0)
    ) {
      let errorMessage = "";

      if (responseErrors.length > 0) {
        errorMessage = responseErrors
          .map((e) => `${e.platform || "Platform"}: ${e.message}`)
          .join("\n");
      } else if (rootError) {
        errorMessage = rootError;
      } else if (
        rootMessage &&
        rootMessage !== "User campaigns retrieved successfully"
      ) {
        errorMessage = rootMessage;
      }

      if (errorMessage) {
        dispatch(
          setToastMessage({
            message: errorMessage,
            severity: "error",
          })
        );
      }
    }
  }, [userCampaignsData, userCampaignsError, dispatch, t]);

  useEffect(() => {
    setDv360InsertionOrderSelections((prev) => {
      let updated = false;
      const next: Record<string, string[]> = { ...prev };

      internalCampaigns.forEach((campaign) => {
        const internalPlatform = resolveAssignedInternalPlatform(campaign);
        if (isDv360InternalPlatform(internalPlatform)) {
          if (!next[campaign.id]) {
            next[campaign.id] = campaign.dv360InsertionOrderIds
              ? [...campaign.dv360InsertionOrderIds]
              : [];
            updated = true;
          }
        }
      });

      Object.keys(next).forEach((campaignId) => {
        const exists = internalCampaigns.some((campaign) => {
          const internalPlatform = resolveAssignedInternalPlatform(campaign);
          return (
            campaign.id === campaignId &&
            isDv360InternalPlatform(internalPlatform)
          );
        });
        if (!exists) {
          delete next[campaignId];
          updated = true;
        }
      });

      return updated ? next : prev;
    });

    setDv360SelectionErrors((prev) => {
      let updated = false;
      const next: Record<string, string> = { ...prev };

      Object.keys(next).forEach((campaignId) => {
        const exists = internalCampaigns.some((campaign) => {
          const internalPlatform = resolveAssignedInternalPlatform(campaign);
          return (
            campaign.id === campaignId &&
            isDv360InternalPlatform(internalPlatform)
          );
        });
        if (!exists) {
          delete next[campaignId];
          updated = true;
        }
      });

      return updated ? next : prev;
    });
  }, [internalCampaigns]);

  // Define error state type to match all form fields
  type FormErrors = {
    displayName: string;
    company: string;
    startDate: string;
    endDate: string;
    goal: string;
    advertiserId: string;
    partnerId: string;
    assignedCampaigns: string;
  };

  // Form validation
  const [errors, setErrors] = useState<FormErrors>({
    displayName: "",
    company: "",
    startDate: "",
    endDate: "",
    goal: "",
    advertiserId: "",
    partnerId: "",
    assignedCampaigns: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCampaignData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (name in errors) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Effect to automatically adjust internal campaign dates when parent dates change
  useEffect(() => {
    // Only run if we have internal campaigns and valid parent dates
    if (
      internalCampaigns.length > 0 &&
      campaignData.startDate &&
      campaignData.endDate
    ) {
      const parentStartDate = campaignData.startDate;
      const parentEndDate = campaignData.endDate;

      // Check each internal campaign and adjust dates if needed
      let updatedCampaigns = false;
      const adjustedCampaignNames: string[] = [];

      const adjustedCampaigns = internalCampaigns.map((campaign) => {
        let campaignStartDate = campaign.startDate;
        let campaignEndDate = campaign.endDate;
        let needsUpdate = false;

        // Adjust start date if before parent start date
        if (campaignStartDate < parentStartDate) {
          campaignStartDate = parentStartDate;
          needsUpdate = true;
        }

        // Adjust end date if after parent end date
        if (campaignEndDate > parentEndDate) {
          campaignEndDate = parentEndDate;
          needsUpdate = true;
        }

        if (needsUpdate) {
          updatedCampaigns = true;
          adjustedCampaignNames.push(campaign.name);
          return asAssignedCampaign({
            ...campaign,
            startDate: campaignStartDate,
            endDate: campaignEndDate,
          });
        }

        return campaign;
      });

      // Update the internal campaigns if any were adjusted
      if (updatedCampaigns) {
        setInternalCampaigns(adjustedCampaigns);

        // Show feedback about the adjustments
        dispatch(
          setToastMessage({
            message: t("validation.internalCampaignsDatesAdjusted", {
              campaigns: adjustedCampaignNames.join(", "),
            }),
            severity: "info",
          })
        );
      }
    }
  }, [
    campaignData.startDate,
    campaignData.endDate,
    internalCampaigns,
    t,
    dispatch,
  ]);

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    console.log(`Select changed: ${name} = ${value}`);

    setCampaignData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "platform") {
      const nextPlatform = value as Platform;
      if (nextPlatform === "CTV") {
        setFilterPlatform(internalSourcePlatform);
      } else {
        setInternalSourcePlatform("DV360");
        setFilterPlatform(nextPlatform);
      }
    }
  };

  const handleInternalSourcePlatformChange = (
    event: SelectChangeEvent<Platform>
  ) => {
    const nextPlatform = event.target.value as Platform;
    setInternalSourcePlatform(nextPlatform);
    if (campaignData.platform === "CTV") {
      setFilterPlatform(nextPlatform);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    setSelectedCompany(event.target.value as string);
    setErrors((prev) => ({
      ...prev,
      company: "",
    }));
  };

  // Fetch available currencies
  const {
    data: currenciesData,
    isLoading: isLoadingCurrencies,
    error: currenciesError,
  } = useGetCurrenciesQuery();

  // Show error toast if currency fetch fails
  useEffect(() => {
    if (currenciesError) {
      dispatch(
        setToastMessage({
          message: t("errors.failedToLoadCurrencies"),
          severity: "error",
        })
      );
      console.error("Failed to load currencies:", currenciesError);
    }
  }, [currenciesError, dispatch, t]);

  // Format currency options from API response
  const currencyOptions =
    currenciesData?.map((currency) => ({
      value: currency.code,
      label: `${currency.code} - ${currency.symbol || ""}`,
    })) || [];

  // Type assertion function to ensure we're working with the correct type
  const asAssignedCampaign = (
    campaign: Partial<AssignedCampaign> & {
      id: string;
      name: string;
      platform: components["schemas"]["Platform"];
    }
  ): AssignedCampaign => campaign as AssignedCampaign;

  // Function to add a campaign to the assigned list
  const handleAddCampaign = (campaign: Campaign) => {
    // Check if campaign is already in the list
    if (internalCampaigns.some((c) => c.id === campaign.id)) {
      return;
    }

    const { advertiserId, campaignId } = getDv360Identifiers(campaign);
    const defaultInternalPlatform = isCtvParent
      ? mapPlatformToInternalPlatform(internalSourcePlatform)
      : mapPlatformToInternalPlatform(
          campaign.originalPlatform || campaign.platform
        );
    const isDv360Internal = isDv360InternalPlatform(defaultInternalPlatform);

    // Add campaign to the list with default values
    setInternalCampaigns((prev: AssignedCampaign[]) => [
      ...prev,
      asAssignedCampaign({
        ...campaign,
        margin: 0,
        startDate: campaignData.startDate,
        endDate: campaignData.endDate,
        advertiserId,
        internalId: campaignId,
        internalPlatform: defaultInternalPlatform,
        dv360InsertionOrderIds: isDv360Internal
          ? dv360InsertionOrderSelections[campaign.id] || []
          : [],
      }),
    ]);

    if (isDv360Internal) {
      setDv360InsertionOrderSelections((prev) => {
        if (prev[campaign.id]) {
          return prev;
        }
        return {
          ...prev,
          [campaign.id]: [],
        };
      });
    }
  };

  // Function to remove a campaign from the assigned list
  const handleRemoveCampaign = (campaignId: string) => {
    setInternalCampaigns((prev: AssignedCampaign[]) =>
      prev.filter((c: AssignedCampaign) => c.id !== campaignId)
    );

    setDv360InsertionOrderSelections((prev) => {
      if (!prev[campaignId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });

    setDv360SelectionErrors((prev) => {
      if (!prev[campaignId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });
  };

  const handleUpdateAssignedCampaign = (updatedCampaign: AssignedCampaign) => {
    const previousCampaign = internalCampaigns.find(
      (campaign) => campaign.id === updatedCampaign.id
    );
    const previousPlatform = previousCampaign
      ? resolveAssignedInternalPlatform(previousCampaign)
      : undefined;
    const nextPlatform = resolveAssignedInternalPlatform(updatedCampaign);

    setInternalCampaigns((prev) =>
      prev.map((campaign) =>
        campaign.id === updatedCampaign.id
          ? asAssignedCampaign(updatedCampaign)
          : campaign
      )
    );

    if (
      isDv360InternalPlatform(nextPlatform) &&
      !isDv360InternalPlatform(previousPlatform)
    ) {
      setDv360InsertionOrderSelections((prev) => {
        if (prev[updatedCampaign.id]) {
          return prev;
        }
        return {
          ...prev,
          [updatedCampaign.id]: [],
        };
      });
    }

    if (
      !isDv360InternalPlatform(nextPlatform) &&
      isDv360InternalPlatform(previousPlatform)
    ) {
      setDv360InsertionOrderSelections((prev) => {
        if (!prev[updatedCampaign.id]) {
          return prev;
        }
        const next = { ...prev };
        delete next[updatedCampaign.id];
        return next;
      });
      setDv360SelectionErrors((prev) => {
        if (!prev?.[updatedCampaign.id]) {
          return prev;
        }
        const next = { ...prev };
        delete next[updatedCampaign.id];
        return next;
      });
    }
  };

  const handleDv360InsertionOrderChange = (
    campaignId: string,
    insertionOrderIds: string[]
  ) => {
    setDv360InsertionOrderSelections((prev) => ({
      ...prev,
      [campaignId]: insertionOrderIds,
    }));

    setInternalCampaigns((prev) =>
      prev.map((campaign) =>
        campaign.id === campaignId
          ? {
              ...campaign,
              dv360InsertionOrderIds: insertionOrderIds,
            }
          : campaign
      )
    );

    const campaign = internalCampaigns.find((c) => c.id === campaignId);
    const campaignName = campaign?.name ?? campaignId;

    setDv360SelectionErrors((prev) => {
      const next = { ...prev };
      if (insertionOrderIds.length === 0) {
        next[campaignId] = t("campaigns.errors.dv360InsertionOrdersRequired", {
          defaultValue: "Select at least one insertion order for {{campaign}}.",
          campaign: campaignName,
        });
      } else {
        delete next[campaignId];
      }
      return next;
    });
  };

  const validateForm = () => {
    const newErrors: FormErrors = {
      displayName: "",
      company: "",
      startDate: "",
      endDate: "",
      goal: "",
      advertiserId: "",
      partnerId: "",
      assignedCampaigns: "",
    };

    // Required fields validation
    if (!campaignData.displayName) {
      newErrors.displayName = t("validation.required");
    }
    if (!selectedCompany) {
      newErrors.company = t("validation.required");
    }
    if (!campaignData.startDate) {
      newErrors.startDate = t("validation.required");
    }
    if (!campaignData.endDate) {
      newErrors.endDate = t("validation.required");
    }
    if (!campaignData.goal) {
      newErrors.goal = t("validation.required");
    }

    // Parent campaign date validation
    if (campaignData.startDate && campaignData.endDate) {
      const startDate = new Date(campaignData.startDate);
      const endDate = new Date(campaignData.endDate);
      if (startDate > endDate) {
        newErrors.startDate = t("validation.endDateAfterStartDate");
      }
    }

    // Internal campaigns validation
    if (internalCampaigns.length === 0) {
      newErrors.assignedCampaigns = t("validation.atLeastOneAssignedCampaign");
    } else {
      internalCampaigns.forEach((campaign) => {
        if (campaign.startDate && campaign.endDate) {
          const startDate = new Date(campaign.startDate);
          const endDate = new Date(campaign.endDate);
          const parentStartDate = new Date(campaignData.startDate);
          const parentEndDate = new Date(campaignData.endDate);

          // Check if internal campaign dates are within parent campaign range
          if (startDate < parentStartDate) {
            newErrors.assignedCampaigns = t(
              "validation.internalCampaignStartDateBeforeParent",
              { campaign: campaign.name }
            );
          }
          if (endDate > parentEndDate) {
            newErrors.assignedCampaigns = t(
              "validation.internalCampaignEndDateAfterParent",
              { campaign: campaign.name }
            );
          }
          // Check if internal campaign end date is greater than or equal to its start date
          if (startDate > endDate) {
            newErrors.assignedCampaigns = t(
              "validation.internalCampaignEndDateAfterStartDate",
              { campaign: campaign.name }
            );
          }
        }
      });
    }

    const newDv360Errors: Record<string, string> = {};
    internalCampaigns
      .filter((campaign) =>
        isDv360InternalPlatform(resolveAssignedInternalPlatform(campaign))
      )
      .forEach((campaign) => {
        const selectedOrders = dv360InsertionOrderSelections[campaign.id] || [];
        if (selectedOrders.length === 0) {
          newDv360Errors[campaign.id] = t(
            "campaigns.errors.dv360InsertionOrdersRequired",
            {
              defaultValue:
                "Select at least one insertion order for {{campaign}}.",
              campaign: campaign.name,
            }
          );
        }
      });

    setDv360SelectionErrors(newDv360Errors);

    setErrors(newErrors);
    const hasFormErrors = Object.values(newErrors).some((error) => !!error);
    const hasDv360Errors = Object.keys(newDv360Errors).length > 0;
    return !hasFormErrors && !hasDv360Errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Format internal campaigns with their boolean metric flags
      const formattedInternalCampaigns = internalCampaigns.map((campaign) => {
        const resolvedInternalPlatform =
          resolveAssignedInternalPlatform(campaign);
        const isDv360Platform =
          resolvedInternalPlatform &&
          isDv360InternalPlatform(resolvedInternalPlatform);

        return {
          displayName: campaign.name,
          internalPlatform: resolvedInternalPlatform,
          internalId: isDv360Platform
            ? (campaign.internalId ?? campaign.id ?? null)
            : campaign.id,
          // Spotify ad sets sync at insertion-order level; campaigns omit the field.
          internalEntityType:
            campaign.entityType === "AD_SET"
              ? ("INSERTIONORDER" as const)
              : undefined,
          margin:
            campaign.margin !== undefined ? Number(campaign.margin) : undefined,
          timezone: campaign.timezone,
          startDate: campaign.startDate,
          endDate: campaign.endDate,
          currency: campaign.currency,
        };
      });

      const formatDateToISO = (dateString: string) => {
        if (dateString.includes("T")) return dateString;
        return `${dateString}T00:00:00.000Z`;
      };

      const aggregatedDv360InsertionOrderIds = Array.from(
        new Set(
          Object.values(dv360InsertionOrderSelections).flat().filter(Boolean)
        )
      );

      await createCampaign({
        ...campaignData,
        goal: campaignData.goal as CampaignGoals,
        startDate: formatDateToISO(campaignData.startDate),
        endDate: formatDateToISO(campaignData.endDate),
        companyId: selectedCompany,
        internalCampaigns: formattedInternalCampaigns.map((campaign) => ({
          displayName: campaign.displayName,
          internalPlatform: campaign.internalPlatform,
          internalId: campaign.internalId,
          internalEntityType: campaign.internalEntityType,
          margin: campaign.margin,
          timezone: campaign.timezone,
          currency: campaign.currency,
          startDate: formatDateToISO(campaign.startDate),
          endDate: formatDateToISO(campaign.endDate),
        })),
        internalCampaignMerges,
        format: campaignData.format,
        dv360InsertionOrderIds: aggregatedDv360InsertionOrderIds,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error creating campaign:", error);
    }
  };

  if (userDb?.role !== "ADMIN") {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: "8px",
          p: 3,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Nunito",
            fontWeight: 700,
            color: theme.palette.grey[900],
          }}
        >
          {t("campaigns.adminOnlyFeature")}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Nunito",
            color: theme.palette.grey[700],
            mb: 2,
          }}
        >
          {t("campaigns.adminOnlyFeatureDescription")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => navigate("/campaigns")}
          sx={{
            textTransform: "none",
            fontFamily: "Nunito",
            fontWeight: 700,
            borderRadius: "50px",
            px: 3,
          }}
        >
          {t("common.back")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        {/* Header with back button */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Button
            startIcon={<KeyboardBackspaceIcon />}
            onClick={() => navigate("/campaigns")}
            sx={{
              textTransform: "none",
              fontFamily: "Nunito",
              fontWeight: 700,
              color: theme.palette.grey[900],
              mr: 2,
            }}
          >
            {t("common.back")}
          </Button>
        </Box>

        {/* Main content */}
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: "8px",
            p: 3,
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Nunito",
              fontWeight: 700,
              color: theme.palette.grey[900],
              pb: 1,
            }}
          >
            {t("campaigns.newCampaign")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {companiesData?.data?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                sx={{
                  fontFamily: "Nunito",
                  fontWeight: 600,
                  color: theme.palette.grey[800],
                  mb: 2,
                }}
              >
                {t("campaigns.noCompanyAssociation")}
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* First row: Name, Company, Platform */}
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                  {/* Display Name */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("campaigns.displayName")} *
                    </Typography>
                    <TextField
                      name="displayName"
                      placeholder={t("campaigns.displayName")}
                      value={campaignData.displayName}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      error={!!errors.displayName}
                      helperText={errors.displayName}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Company */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("campaigns.company")} *
                    </Typography>
                    <FormControl
                      fullWidth
                      error={!!errors.company}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    >
                      <Select
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <Typography
                                sx={{
                                  color: theme.palette.grey[500],
                                  fontFamily: "Nunito",
                                }}
                              >
                                {t("campaigns.selectCompany")}
                              </Typography>
                            );
                          }
                          const company = companiesData?.data?.find(
                            (c) => c.id === selected
                          ) as CompanyWithDisplay | undefined;
                          return company
                            ? company.nameCommercial ||
                                company.nameFiscal ||
                                selected
                            : selected;
                        }}
                      >
                        {isLoadingCompanies ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} />
                          </MenuItem>
                        ) : companiesError ? (
                          <MenuItem disabled>
                            {t("common.errorLoadingData")}
                          </MenuItem>
                        ) : (
                          companiesData?.data?.map((company) => {
                            const companyWithDisplay =
                              company as CompanyWithDisplay;
                            return (
                              <MenuItem key={company.id} value={company.id}>
                                {companyWithDisplay.nameCommercial ||
                                  companyWithDisplay.nameFiscal ||
                                  company.id}
                              </MenuItem>
                            );
                          })
                        )}
                      </Select>
                      {errors.company && (
                        <FormHelperText>{errors.company}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* Platform */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("campaigns.platform")} *
                    </Typography>
                    <FormControl
                      fullWidth
                      error={!!errors.goal}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    >
                      <Select
                        name="platform"
                        value={campaignData.platform}
                        onChange={handleSelectChange}
                      >
                        <MenuItem value="DV360">
                          {t("platforms.dv360")}
                        </MenuItem>
                        <MenuItem value="CTV">
                          {t("platforms.ctv", { defaultValue: "CTV" })}
                        </MenuItem>
                        <MenuItem value="NATADS">
                          {t("platforms.natads")}
                        </MenuItem>
                        <MenuItem value="SPOTIFY_ADS">
                          {t("platforms.spotify")}
                        </MenuItem>
                        <MenuItem value="HIVESTACK">
                          {t("platforms.hivestack")}
                        </MenuItem>
                        <MenuItem value="AMAZON_DSP">
                          {t("platforms.amazonDsp", {
                            defaultValue: "Amazon DSP",
                          })}
                        </MenuItem>
                        <MenuItem value="META">
                          {t("platforms.meta", { defaultValue: "Meta" })}
                        </MenuItem>
                        <MenuItem value="TIKTOK">
                          {t("platforms.tiktok", { defaultValue: "TikTok" })}
                        </MenuItem>
                        <MenuItem value="GOOGLE_ADS">
                          {t("platforms.googleAds", {
                            defaultValue: "Google Ads",
                          })}
                        </MenuItem>
                        <MenuItem value="REDDIT">
                          {t("platforms.reddit", { defaultValue: "Reddit" })}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {isCtvParent && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("campaigns.internalSourcePlatform", {
                        defaultValue: "Source platform for CTV internals",
                      })}
                    </Typography>
                    <FormControl
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    >
                      <Select
                        value={internalSourcePlatform}
                        onChange={handleInternalSourcePlatformChange}
                      >
                        {CTV_SOURCE_PLATFORMS.map((platform) => (
                          <MenuItem key={platform} value={platform}>
                            {getPlatformLabel(platform)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Date Range */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {/* Start Date */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("campaigns.startDate")} *
                    </Typography>
                    <TextField
                      name="startDate"
                      type="date"
                      value={campaignData.startDate}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      error={!!errors.startDate}
                      helperText={errors.startDate}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        max: campaignData.endDate,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* End Date */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("campaigns.endDate")} *
                    </Typography>
                    <TextField
                      name="endDate"
                      type="date"
                      value={campaignData.endDate}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      error={!!errors.endDate}
                      helperText={errors.endDate}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: campaignData.startDate,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "50px",
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& fieldset": {
                            borderColor: theme.palette.grey[300],
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* User Campaigns Section */}
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                        pb: 1,
                      }}
                    >
                      {t("campaigns.userCampaigns") || "Available Campaigns"}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      width: "100%",
                      height: "auto",
                      padding: 1.5,
                      background: theme.palette.background.paper,
                      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
                      borderRadius: 1,
                      border: `1px ${theme.palette.divider} solid`,
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      gap: 1.5,
                      display: "flex",
                    }}
                  >
                    <UserCampaignsTable
                      selectedCompany={selectedCompany}
                      isLoadingUserCampaigns={isLoadingUserCampaigns}
                      userCampaignsData={userCampaignsData}
                      userCampaignsError={userCampaignsError}
                      assignedCampaigns={internalCampaigns}
                      handleAddCampaign={handleAddCampaign}
                      handleRemoveCampaign={handleRemoveCampaign}
                    />
                  </Box>
                </Box>

                {/* Assigned Campaigns Section */}
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 3 }} />
                  <AssignedCampaignsTable
                    assignedCampaigns={internalCampaigns}
                    handleRemoveCampaign={handleRemoveCampaign}
                    updateAssignedCampaign={handleUpdateAssignedCampaign}
                    parentStartDate={campaignData.startDate}
                    parentEndDate={campaignData.endDate}
                    dv360InsertionOrderSelections={
                      dv360InsertionOrderSelections
                    }
                    onDv360InsertionOrderChange={
                      handleDv360InsertionOrderChange
                    }
                    dv360SelectionErrors={dv360SelectionErrors}
                    enableInternalPlatformSelection={isCtvParent}
                  />
                  {Object.values(dv360SelectionErrors).length > 0 && (
                    <FormHelperText error sx={{ mt: 2 }}>
                      {t("campaigns.errors.dv360InsertionOrdersRequired", {
                        defaultValue:
                          "Select at least one insertion order for each DV360 campaign.",
                        campaign: "",
                      })}
                    </FormHelperText>
                  )}
                  <Box sx={{ mt: 3 }}>
                    <CampaignMergeEditor
                      internalCampaigns={internalCampaigns}
                      merges={internalCampaignMerges}
                      onUpdateMerges={setInternalCampaignMerges}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Submit Button moved to end of page */}
            </Box>
          )}
        </Box>

        {/* User Campaigns Section moved below the row with three input elements */}

        {companiesData?.data?.length === 0 ? null : (
          <>
            {/* Format dropdown - only shown for DV360 */}
            {filterPlatform === "DV360" && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  mt: 4,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "50%",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("campaigns.format")}
                  </Typography>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[300],
                        },
                      },
                    }}
                  >
                    <Select
                      name="format"
                      value={campaignData.format}
                      onChange={handleSelectChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography
                              sx={{
                                color: theme.palette.grey[500],
                                fontFamily: "Nunito",
                              }}
                            >
                              {t("campaigns.selectFormat")}
                            </Typography>
                          );
                        }
                        return selected;
                      }}
                    >
                      {isLoadingCampaignFormats ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                        </MenuItem>
                      ) : (
                        campaignFormatsData?.data?.formats.map((format) => (
                          <MenuItem key={format} value={format}>
                            {format}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* Additional Fields Row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                mt: 4,
              }}
            >
              {/* Currency Selection */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("campaigns.currency")}
                </Typography>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[300],
                      },
                    },
                  }}
                >
                  {isLoadingCurrencies ? (
                    <Box display="flex" justifyContent="center" py={1}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Select
                      name="currency_enum"
                      value={campaignData.currency_enum}
                      onChange={handleSelectChange}
                      disabled={isLoadingCurrencies}
                    >
                      {currencyOptions.map((currency) => (
                        <MenuItem key={currency.value} value={currency.value}>
                          {currency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormControl>
              </Box>

              {/* Margin Level */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("campaigns.goal")} *
                </Typography>
                <FormControl
                  fullWidth
                  error={!!errors.goal}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[300],
                      },
                    },
                  }}
                >
                  <Select
                    name="goal"
                    value={campaignData.goal}
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="CPC">{"CPC"}</MenuItem>
                    <MenuItem value="CPM">{"CPM"}</MenuItem>
                    <MenuItem value="CPV">{"CPV"}</MenuItem>
                  </Select>
                  {errors.goal && (
                    <FormHelperText>{errors.goal}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Visibility for clients — internal roles only */}
            {!isClient && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={campaignData.visibleToClients}
                      onChange={(e) =>
                        setCampaignData((prev) => ({
                          ...prev,
                          visibleToClients: e.target.checked,
                        }))
                      }
                      color="success"
                    />
                  }
                  label={t(
                    "campaigns.visibleToClients",
                    "Visible para el cliente"
                  )}
                />
              </Box>
            )}

            {/* Submit Button at end of page */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isCreating}
                onClick={handleSubmit}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {isCreating ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("common.create")
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NewCampaign;
